import { getTabColOrData } from '@/api/user'
import request from '@/utils/request'
import createFormFieldSettingsService, {
    getFieldKey,
    mergeFieldSettings,
    normalizeScope,
    normalizeSortBy,
    resetFieldSettings,
    sanitizeFieldSettings,
} from './formFieldSettingsService'

const SYSTEM_COLUMN_KEYS = ['rownum', 'index', 'oper', 'oper0', 'expand']

function cloneRows(rows) {
    return (rows || []).map(row => Object.assign({}, row))
}

function fieldKeyIdentity(value) {
    if (value === undefined || value === null) return ''
    return String(value).trim().toUpperCase()
}

function rowKeyIdentity(row) {
    return fieldKeyIdentity(getFieldKey(row))
}

function isFixedMarker(value) {
    const marker = String(value === undefined || value === null ? '' : value).trim().toLowerCase()
    return marker === 'left' || marker === 'right' || marker === 'l' || marker === 'r' || marker === 'y'
        || marker === 'yes' || marker === 'true' || marker === '1'
}

function isFixedColumn(column) {
    if (!column) return false
    return [column.fixed, column.FIXED, column.fixedSide, column.FIXEDSIDE,
        column.fix, column.FIX, column.ISFIX, column.isfix].some(isFixedMarker)
}

function positiveNumber(value) {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : 0
}

function isExpansionColumn(column) {
    const type = String(column && (column.type || column.TYPE || column.columnType) || '').toLowerCase()
    return type === 'expand' || column && (column.expand === true || column.EXPAND === true)
}

function configuredFixedKeys(config) {
    return [].concat(
        config && config.fixedFieldKeys || [],
        config && config.generatedFixedColumnKeys || [],
        config && config.leftFixedColumnKeys || [],
        config && config.rightFixedColumnKeys || []
    ).map(key => String(key).trim().toUpperCase())
}

function isExcludedColumn(column, config) {
    const key = getFieldKey(column)
    const keyIdentity = String(key || '').trim().toUpperCase()
    return !key
        || SYSTEM_COLUMN_KEYS.includes(keyIdentity.toLowerCase())
        || configuredFixedKeys(config).includes(keyIdentity)
        || isExpansionColumn(column)
        || isFixedColumn(column)
}

export function getGroupedColumnLeaves(columns, config = {}) {
    const leaves = []
    const visit = items => {
        (items || []).forEach(column => {
            if (column && Array.isArray(column.children) && column.children.length) {
                visit(column.children)
                return
            }
            if (column && !isExcludedColumn(column, config)) leaves.push(column)
        })
    }
    visit(columns)
    return leaves
}

function getAllColumnLeaves(columns) {
    const leaves = []
    const visit = items => {
        (items || []).forEach(column => {
            if (column && Array.isArray(column.children) && column.children.length) {
                visit(column.children)
                return
            }
            if (column) leaves.push(column)
        })
    }
    visit(columns)
    return leaves
}

export function applyGroupedFieldSettings(columns, rows, config = {}) {
    const settingsByKey = {}
    normalizeSortBy(rows || []).forEach(row => {
        const key = getFieldKey(row)
        if (key) settingsByKey[fieldKeyIdentity(key)] = row
    })

    const applyLevel = items => {
        const leafSlots = []
        const sortableLeaves = []
        const columns = items || []
        columns.forEach((column, index) => {
            if (column && Array.isArray(column.children) && column.children.length) {
                applyLevel(column.children)
                return
            }
            if (!column || isExcludedColumn(column, config)) return
            const setting = settingsByKey[rowKeyIdentity(column)]
            if (setting && positiveNumber(setting.WIDTH || setting.width)) {
                column.width = Number(setting.WIDTH || setting.width)
                column.WIDTH = Number(setting.WIDTH || setting.width)
            }
            leafSlots.push(index)
            sortableLeaves.push(column)
        })
        sortableLeaves.sort((left, right) => {
            const leftSetting = settingsByKey[rowKeyIdentity(left)]
            const rightSetting = settingsByKey[rowKeyIdentity(right)]
            const leftOrder = leftSetting ? Number(leftSetting.SORTBY || leftSetting.sortby) : 1000000
            const rightOrder = rightSetting ? Number(rightSetting.SORTBY || rightSetting.sortby) : 1000000
            return leftOrder - rightOrder
        })
        leafSlots.forEach((slot, index) => {
            if (typeof items.splice === 'function') items.splice(slot, 1, sortableLeaves[index])
            else items[slot] = sortableLeaves[index]
        })
    }
    applyLevel(columns || [])
    return columns
}

function normalizeGridDefinitions(options) {
    const configuredGrids = options.grids || options.tables
    const keys = configuredGrids ? Object.keys(configuredGrids) : []
    const defaultKey = options.defaultConfigKey || keys[0] || 'default'
    const source = configuredGrids || { [defaultKey]: options }
    const definitions = {}
    Object.keys(source).forEach(key => {
        const item = source[key] || {}
        definitions[key] = Object.assign({}, options, item, {
            key,
            settingFormId: item.settingFormId || item.formId || options.settingFormId || options.formId || '',
            fieldIdseq: item.fieldIdseq || options.fieldIdseq || '',
            fieldLstseq: item.fieldLstseq || options.fieldLstseq || '',
            refItmseq: item.refItmseq || item.scopeRefItmseq || options.refItmseq || options.scopeRefItmseq || '',
            mainColumnKey: item.mainColumnKey || options.mainColumnKey || 'tabArr',
            tableColumnKey: item.tableColumnKey || options.tableColumnKey || 'vueCol',
            initColumnKey: item.initColumnKey || options.initColumnKey || 'initCol',
            tableRef: item.tableRef || options.tableRef || 'tableRef',
            columnHiddenOptionKey: item.columnHiddenOptionKey || options.columnHiddenOptionKey || 'columnHiddenOption',
            lockedFieldKeys: [].concat(item.lockedFieldKeys || item.disabledFieldKeys || options.lockedFieldKeys || options.disabledFieldKeys || []),
            fixedFieldKeys: [].concat(item.fixedFieldKeys || options.fixedFieldKeys || []),
            leftFixedColumnKeys: [].concat(item.leftFixedColumnKeys || options.leftFixedColumnKeys || []),
            rightFixedColumnKeys: [].concat(item.rightFixedColumnKeys || options.rightFixedColumnKeys || []),
            generatedFixedColumnKeys: [].concat(item.generatedFixedColumnKeys || options.generatedFixedColumnKeys || []),
            syncMainColumnSettings: Boolean(item.syncMainColumnSettings || options.syncMainColumnSettings),
            syncInitialColumnSettings: Boolean(item.syncInitialColumnSettings || options.syncInitialColumnSettings),
            groupedColumnChildren: Boolean(item.groupedColumnChildren || options.groupedColumnChildren),
            dragGroupField: item.dragGroupField || options.dragGroupField || '',
        })
    })
    return { defaultKey, definitions }
}

function rawMetadataRows(component, config) {
    return component[config.initColumnKey] || []
}

function metadataRowsFor(component, config) {
    const rows = rawMetadataRows(component, config)
    return config.groupedColumnChildren ? getGroupedColumnLeaves(rows, config) : rows
}

function metadataFieldForScope(rows) {
    return (rows || []).find(item => item && fieldKeyIdentity(item.THIDE !== undefined ? item.THIDE : item.thide) === 'N') || (rows || [])[0] || {}
}

function configurableMetadataRows(component, config) {
    const dynamicFixedKeys = component && typeof component.getFieldSettingFixedKeys === 'function'
        ? component.getFieldSettingFixedKeys(config.key)
        : configuredFixedKeys(config)
    const filterConfig = dynamicFixedKeys.length
        ? Object.assign({}, config, { fixedFieldKeys: dynamicFixedKeys })
        : config
    return metadataRowsFor(component, filterConfig).filter(item => {
        if (!item || isExcludedColumn(item, filterConfig)) return false
        const hideFlag = fieldKeyIdentity(item.THIDE !== undefined ? item.THIDE : item.thide)
        return hideFlag === 'N'
    })
}

export default function createVeTableFieldSettingMixin(options = {}) {
    const setup = normalizeGridDefinitions(options)
    const defaultConfigKey = setup.defaultKey
    const definitions = setup.definitions

    return {
        data() {
            return {
                fieldSettingsByKey: {},
                fieldSettingDialog: {
                    visible: false,
                    key: defaultConfigKey,
                    rows: [],
                    loading: false,
                    lockedFieldKeys: [],
                    dragGroupField: '',
                    conflicts: [],
                    error: '',
                    refreshError: false,
                    requestVersion: 0,
                },
                userFieldSetRows: [],
                fieldWidthMap: {},
                fieldSortMap: {},
                fieldVisibleMap: {},
                columnWidthResizeOption: {
                    enable: true,
                    sizeChange: payload => this.__veTableCollectFieldWidth(payload, defaultConfigKey),
                },
            }
        },
        watch: {
            'fieldSettingDialog.visible'(visible, previousVisible) {
                if (visible === false && previousVisible !== false) {
                    this.__veTableInvalidateFieldSettingDialogRequest()
                }
            },
        },
        methods: {
            getFieldKey,
            getFieldSettingConfig(configKey) {
                const key = configKey || this.fieldSettingDialog.key || defaultConfigKey
                return definitions[key] || definitions[defaultConfigKey]
            },
            getFieldSettingState(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                if (!this.fieldSettingsByKey[config.key]) {
                    this.$set(this.fieldSettingsByKey, config.key, {
                        rows: [],
                        storedRows: [],
                        widthMap: {},
                        runtimeWidthMap: {},
                        visibleMap: {},
                        sortMap: {},
                        conflicts: [],
                        loading: false,
                        requestVersion: 0,
                    })
                }
                return this.fieldSettingsByKey[config.key]
            },
            getFieldSettingScope(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const firstField = metadataFieldForScope(metadataRowsFor(this, config))
                const cookieUserNo = this.$cookies && typeof this.$cookies.get === 'function'
                    ? this.$cookies.get('v_telno')
                    : ''
                const cookieCorpId = this.$cookies && typeof this.$cookies.get === 'function'
                    ? this.$cookies.get('v_corpid')
                    : ''
                return normalizeScope({
                    settingFormId: config.settingFormId,
                    fieldIdseq: config.fieldIdseq || firstField.IDSEQ || firstField.idseq || config.settingFormId,
                    fieldLstseq: config.fieldLstseq || firstField.LSTSEQ || firstField.lstseq,
                    refItmseq: config.refItmseq,
                    userNo: config.userNo || this.v_username || this.v_userno || cookieUserNo,
                    corpId: config.corpId || this.v_corpid || this.v_corpId || cookieCorpId,
                })
            },
            getFieldSettingLockedKeys(configKey) {
                return this.getFieldSettingConfig(configKey).lockedFieldKeys.slice()
            },
            getFieldSettingFixedKeys(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const identities = {}
                const fixedKeys = []
                const addKey = key => {
                    const identity = fieldKeyIdentity(key)
                    if (!identity || identities[identity]) return
                    identities[identity] = true
                    fixedKeys.push(identity)
                }
                configuredFixedKeys(config).forEach(addKey)
                const collect = list => getAllColumnLeaves(list).forEach(column => {
                    if (isFixedColumn(column)) addKey(getFieldKey(column))
                })
                collect(this[config.tableColumnKey])
                collect(this[config.mainColumnKey])
                collect(rawMetadataRows(this, config))
                return fixedKeys
            },
            getFieldSettingApiContext() {
                return { url: this.v_url }
            },
            getFormFieldSettingsService() {
                return createFormFieldSettingsService({ getTabColOrData, request })
            },
            getFieldSettingDisplayName(item) {
                if (this.$translate && typeof this.$translate.getColTitle === 'function') {
                    const language = this.$store && this.$store.state ? this.$store.state.lang : 'CN'
                    return this.$translate.getColTitle(item, language)
                }
                return item && (item.FLDNM || item.CNAME || item.title || getFieldKey(item))
            },
            beginFieldSettingRequest(configKey) {
                const state = this.getFieldSettingState(configKey)
                state.requestVersion = Number(state.requestVersion || 0) + 1
                return state.requestVersion
            },
            isCurrentFieldSettingRequest(configKey, version) {
                return Number(this.getFieldSettingState(configKey).requestVersion || 0) === version
            },
            __veTableBeginFieldSettingDialogRequest(configKey) {
                this.fieldSettingDialog.key = configKey
                this.fieldSettingDialog.requestVersion = Number(this.fieldSettingDialog.requestVersion || 0) + 1
                return this.fieldSettingDialog.requestVersion
            },
            beginFieldSettingDialogRequest(configKey) {
                return this.__veTableBeginFieldSettingDialogRequest(configKey)
            },
            __veTableIsCurrentFieldSettingDialogRequest(configKey, version) {
                return this.fieldSettingDialog.key === configKey
                    && Number(this.fieldSettingDialog.requestVersion || 0) === version
            },
            isCurrentFieldSettingDialogRequest(configKey, version) {
                return this.__veTableIsCurrentFieldSettingDialogRequest(configKey, version)
            },
            __veTableInvalidateFieldSettingDialogRequest() {
                const configKey = this.fieldSettingDialog.key || defaultConfigKey
                const version = this.__veTableBeginFieldSettingDialogRequest(configKey)
                this.fieldSettingDialog.loading = false
                return version
            },
            decorateFieldSettingRows(rows, configKey) {
                const config = this.getFieldSettingConfig(configKey)
                if (!config.dragGroupField) return cloneRows(rows)
                const metadataByKey = {}
                metadataRowsFor(this, config).forEach(field => { metadataByKey[rowKeyIdentity(field)] = field })
                return (rows || []).map(row => {
                    const metadata = metadataByKey[rowKeyIdentity(row)] || {}
                    return Object.assign({}, row, {
                        __fieldSettingDragGroup: String(
                            metadata[config.dragGroupField]
                            || metadata[String(config.dragGroupField).toLowerCase()]
                            || ''
                        ),
                    })
                })
            },
            buildFieldSettingMaps(rows) {
                const visibleMap = {}
                const widthMap = {}
                const sortMap = {}
                const fieldRows = rows || []
                fieldRows.forEach(row => {
                    const key = getFieldKey(row)
                    if (!key) return
                    const identity = fieldKeyIdentity(key)
                    visibleMap[identity] = fieldKeyIdentity(row.VISFLAG || row.visflag) !== 'N'
                    // Keep the raw-key aliases for legacy page builders that
                    // still read fieldWidthMap/fieldVisibleMap directly.
                    visibleMap[key] = visibleMap[identity]
                    if (positiveNumber(row.WIDTH || row.width)) widthMap[identity] = positiveNumber(row.WIDTH || row.width)
                    if (positiveNumber(row.WIDTH || row.width)) widthMap[key] = widthMap[identity]
                    if (positiveNumber(row.SORTBY || row.sortby)) sortMap[identity] = Number(row.SORTBY || row.sortby)
                    if (positiveNumber(row.SORTBY || row.sortby)) sortMap[key] = sortMap[identity]
                })
                return { visibleMap, widthMap, sortMap }
            },
            syncFieldSettingState(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const state = this.getFieldSettingState(config.key)
                const maps = this.buildFieldSettingMaps(state.rows)
                state.visibleMap = maps.visibleMap
                state.widthMap = maps.widthMap
                state.sortMap = maps.sortMap
                if (config.key === defaultConfigKey) {
                    this.userFieldSetRows = cloneRows(state.storedRows)
                    this.fieldWidthMap = Object.assign({}, maps.widthMap)
                    this.fieldSortMap = Object.assign({}, maps.sortMap)
                    this.fieldVisibleMap = Object.assign({}, maps.visibleMap)
                }
                return state
            },
            __veTableLoadFieldSettings(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const state = this.getFieldSettingState(config.key)
                const version = this.beginFieldSettingRequest(config.key)
                state.loading = true
                return this.getFormFieldSettingsService().load(
                    this.getFieldSettingScope(config.key),
                    this.getFieldSettingApiContext(),
                    configurableMetadataRows(this, config),
                    {
                        lockedFieldKeys: this.getFieldSettingLockedKeys(config.key),
                        fixedFieldKeys: this.getFieldSettingFixedKeys(config.key),
                        widthMap: state.runtimeWidthMap,
                        getDisplayName: item => this.getFieldSettingDisplayName(item),
                    }
                ).then(result => {
                    if (!this.isCurrentFieldSettingRequest(config.key, version)) {
                        return Object.assign({}, result, { stale: true })
                    }
                    state.rows = this.decorateFieldSettingRows(result.rows, config.key)
                    state.storedRows = cloneRows(result.storedRows)
                    state.conflicts = (result.conflicts || []).slice()
                    this.syncFieldSettingState(config.key)
                    if ((this[config.tableColumnKey] || []).length) this.__veTableApplyFieldSettingRows(config.key)
                    return Object.assign({}, result, { stale: false })
                }).catch(error => {
                    if (!this.isCurrentFieldSettingRequest(config.key, version)) {
                        return { rows: cloneRows(state.rows), storedRows: cloneRows(state.storedRows), conflicts: [], stale: true, error }
                    }
                    throw error
                }).finally(() => {
                    if (this.isCurrentFieldSettingRequest(config.key, version)) state.loading = false
                })
            },
            loadFieldSettings(configKey) {
                return this.__veTableLoadFieldSettings(configKey)
            },
            loadInitialFieldSettings(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const state = this.getFieldSettingState(config.key)
                return this.__veTableLoadFieldSettings(config.key).catch(error => {
                    const fallback = mergeFieldSettings(
                        configurableMetadataRows(this, config), [], this.getFieldSettingScope(config.key),
                        {
                            lockedFieldKeys: this.getFieldSettingLockedKeys(config.key),
                            fixedFieldKeys: this.getFieldSettingFixedKeys(config.key),
                            getDisplayName: item => this.getFieldSettingDisplayName(item),
                        }
                    )
                    state.rows = this.decorateFieldSettingRows(fallback.rows, config.key)
                    state.storedRows = []
                    state.runtimeWidthMap = {}
                    state.conflicts = []
                    state.loading = false
                    this.syncFieldSettingState(config.key)
                    return { rows: cloneRows(state.rows), storedRows: [], conflicts: [], fallback: true, error }
                })
            },
            hasFieldSetting(map, key) {
                return Object.prototype.hasOwnProperty.call(map || {}, fieldKeyIdentity(key))
            },
            getFieldWidth(key, defaultWidth, configKey) {
                const widthMap = this.getFieldSettingState(configKey).widthMap
                return positiveNumber(widthMap[fieldKeyIdentity(key)]) || positiveNumber(defaultWidth)
            },
            getFieldVisible(key, defaultVisible, configKey) {
                const visibleMap = this.getFieldSettingState(configKey).visibleMap
                const identity = fieldKeyIdentity(key)
                return this.hasFieldSetting(visibleMap, identity) ? visibleMap[identity] : defaultVisible
            },
            sortFieldRows(rows, configKey) {
                const state = this.getFieldSettingState(configKey)
                return (rows || []).map((item, index) => Object.assign({ __fieldSettingsIndex: index }, item)).sort((left, right) => {
                    const leftKey = getFieldKey(left)
                    const rightKey = getFieldKey(right)
                    const leftIdentity = fieldKeyIdentity(leftKey)
                    const rightIdentity = fieldKeyIdentity(rightKey)
                    const leftSort = this.hasFieldSetting(state.sortMap, leftIdentity)
                        ? Number(state.sortMap[leftIdentity]) : 1000000 + Number(left.SORTBY || left.sortby || left.__fieldSettingsIndex || 0)
                    const rightSort = this.hasFieldSetting(state.sortMap, rightIdentity)
                        ? Number(state.sortMap[rightIdentity]) : 1000000 + Number(right.SORTBY || right.sortby || right.__fieldSettingsIndex || 0)
                    return leftSort === rightSort ? left.__fieldSettingsIndex - right.__fieldSettingsIndex : leftSort - rightSort
                }).map(item => {
                    const next = Object.assign({}, item)
                    delete next.__fieldSettingsIndex
                    return next
                })
            },
            buildFieldSettingRows(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const state = this.getFieldSettingState(config.key)
                if (!state.rows.length) {
                    const result = mergeFieldSettings(
                        configurableMetadataRows(this, config), state.storedRows, this.getFieldSettingScope(config.key),
                        {
                            lockedFieldKeys: this.getFieldSettingLockedKeys(config.key),
                            fixedFieldKeys: this.getFieldSettingFixedKeys(config.key),
                            widthMap: state.runtimeWidthMap,
                            getDisplayName: item => this.getFieldSettingDisplayName(item),
                        }
                    )
                    state.rows = this.decorateFieldSettingRows(result.rows, config.key)
                    state.conflicts = result.conflicts || []
                    this.syncFieldSettingState(config.key)
                }
                return cloneRows(state.rows)
            },
            __veTableApplyRuntimeColumnVisible(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const visibleKeys = []
                const hiddenKeys = []
                const runtimeColumns = config.groupedColumnChildren
                    ? getAllColumnLeaves(this[config.tableColumnKey])
                    : (this[config.tableColumnKey] || [])
                const runtimeKeyByIdentity = {}
                runtimeColumns.forEach(column => {
                    const runtimeKey = getFieldKey(column)
                    if (runtimeKey) runtimeKeyByIdentity[fieldKeyIdentity(runtimeKey)] = runtimeKey
                })
                configurableMetadataRows(this, config).forEach(item => {
                    const key = getFieldKey(item)
                    const runtimeKey = runtimeKeyByIdentity[fieldKeyIdentity(key)] || key
                    if (this.getFieldVisible(key, true, config.key)) visibleKeys.push(runtimeKey)
                    else hiddenKeys.push(runtimeKey)
                })
                const hiddenOption = this[config.columnHiddenOptionKey]
                if (hiddenOption) hiddenOption.defaultHiddenColumnKeys = hiddenKeys
                this.$nextTick(() => {
                    const ref = this.$refs && this.$refs[config.tableRef]
                    const table = Array.isArray(ref) ? ref[0] : ref
                    if (!table) return
                    if (visibleKeys.length && typeof table.showColumnsByKeys === 'function') table.showColumnsByKeys(visibleKeys)
                    if (typeof table.hideColumnsByKeys === 'function') table.hideColumnsByKeys(hiddenKeys)
                })
                return { visibleKeys, hiddenKeys }
            },
            applyRuntimeColumnVisible(configKey) {
                return this.__veTableApplyRuntimeColumnVisible(configKey)
            },
            __veTableApplyColumnOrder(configKey, rows) {
                const config = this.getFieldSettingConfig(configKey)
                const orderMap = {}
                normalizeSortBy(rows || []).forEach(row => { orderMap[rowKeyIdentity(row)] = Number(row.SORTBY || row.sortby) })
                const columns = this[config.tableColumnKey] || []
                const movableSlots = []
                const movableColumns = []
                columns.forEach((column, index) => {
                    if (isExcludedColumn(column, config)) return
                    movableSlots.push(index)
                    movableColumns.push(column)
                })
                movableColumns.sort((first, second) => (
                    (orderMap[rowKeyIdentity(first)] || 1000000) - (orderMap[rowKeyIdentity(second)] || 1000000)
                ))
                const reordered = columns.slice()
                movableSlots.forEach((slot, index) => { reordered[slot] = movableColumns[index] })
                this[config.tableColumnKey] = reordered
                if (config.syncMainColumnSettings) this[config.mainColumnKey] = this.sortFieldRows(this[config.mainColumnKey], config.key)
            },
            applyColumnOrder(configKey, rows) {
                return this.__veTableApplyColumnOrder(configKey, rows)
            },
            __veTableApplyFieldSettingRows(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const state = this.getFieldSettingState(config.key)
                const lists = [this[config.tableColumnKey]]
                if (config.syncMainColumnSettings) lists.push(this[config.mainColumnKey])
                if (config.syncInitialColumnSettings) lists.push(this[config.initColumnKey])
                const fixedKeys = new Set(this.getFieldSettingFixedKeys(config.key))
                lists.forEach(list => {
                    const items = (config.groupedColumnChildren
                        ? getGroupedColumnLeaves(list, config)
                        : (list || []).filter(item => !isExcludedColumn(item, config)))
                        .filter(item => !fixedKeys.has(rowKeyIdentity(item)))
                    items.forEach(item => {
                        const setting = state.rows.find(row => rowKeyIdentity(row) === rowKeyIdentity(item))
                        if (!setting || !positiveNumber(setting.WIDTH)) return
                        this.$set(item, 'width', Number(setting.WIDTH))
                        this.$set(item, 'WIDTH', Number(setting.WIDTH))
                    })
                })
                if (config.groupedColumnChildren) applyGroupedFieldSettings(this[config.tableColumnKey], state.rows, config)
                else this.__veTableApplyColumnOrder(config.key, state.rows)
                this.__veTableApplyRuntimeColumnVisible(config.key)
            },
            applyFieldSettingRows(configKey) {
                return this.__veTableApplyFieldSettingRows(configKey)
            },
            __veTableCollectFieldWidth(payload, configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const column = payload && payload.column
                const key = getFieldKey(column)
                const width = positiveNumber(payload && payload.columnWidth)
                if (!key || isExcludedColumn(column, config) || !width) return
                const state = this.getFieldSettingState(config.key)
                state.runtimeWidthMap = Object.assign({}, state.runtimeWidthMap, { [fieldKeyIdentity(key)]: width })
                const lists = [state.rows]
                const tableColumns = config.groupedColumnChildren
                    ? getGroupedColumnLeaves(this[config.tableColumnKey], config) : this[config.tableColumnKey]
                lists.push(tableColumns)
                if (config.syncMainColumnSettings) lists.push(this[config.mainColumnKey])
                if (config.syncInitialColumnSettings) lists.push(this[config.initColumnKey])
                lists.forEach(list => {
                    (list || []).forEach(item => {
                        if (rowKeyIdentity(item) !== fieldKeyIdentity(key)) return
                        this.$set(item, 'width', width)
                        this.$set(item, 'WIDTH', width)
                    })
                })
                if (this.fieldSettingDialog.key === config.key && this.fieldSettingDialog.rows.length) {
                    this.fieldSettingDialog.rows.forEach(item => {
                        if (rowKeyIdentity(item) !== fieldKeyIdentity(key)) return
                        this.$set(item, 'width', width)
                        this.$set(item, 'WIDTH', width)
                    })
                }
                this.syncFieldSettingState(config.key)
            },
            collectFieldWidth(payload, configKey) {
                return this.__veTableCollectFieldWidth(payload, configKey)
            },
            getColumnWidthResizeOption(configKey) {
                return { enable: true, sizeChange: payload => this.__veTableCollectFieldWidth(payload, configKey) }
            },
            updateFieldSettingsDraft(rows) {
                if (this.fieldSettingDialog.loading) return
                const config = this.getFieldSettingConfig(this.fieldSettingDialog.key)
                const normalized = normalizeSortBy(sanitizeFieldSettings(rows, this.getFieldSettingLockedKeys(config.key)))
                this.fieldSettingDialog.rows = this.decorateFieldSettingRows(normalized, config.key)
            },
            openFieldSettingsDialog(configKey) {
                const config = this.getFieldSettingConfig(configKey)
                const state = this.getFieldSettingState(config.key)
                const version = this.__veTableBeginFieldSettingDialogRequest(config.key)
                this.fieldSettingDialog.visible = true
                this.fieldSettingDialog.loading = true
                this.fieldSettingDialog.rows = []
                this.fieldSettingDialog.conflicts = []
                this.fieldSettingDialog.error = ''
                this.fieldSettingDialog.refreshError = false
                this.fieldSettingDialog.lockedFieldKeys = this.getFieldSettingLockedKeys(config.key)
                this.fieldSettingDialog.dragGroupField = config.dragGroupField
                return this.__veTableLoadFieldSettings(config.key).then(result => {
                    if (result.stale || !this.__veTableIsCurrentFieldSettingDialogRequest(config.key, version)) return result
                    this.fieldSettingDialog.rows = cloneRows(state.rows)
                    this.fieldSettingDialog.conflicts = (result.conflicts || []).slice()
                    return result
                }).catch(error => {
                    if (!this.__veTableIsCurrentFieldSettingDialogRequest(config.key, version)) return { stale: true, error }
                    this.fieldSettingDialog.error = error.message || '字段设置加载失败。'
                    throw error
                }).finally(() => {
                    if (this.__veTableIsCurrentFieldSettingDialogRequest(config.key, version)) this.fieldSettingDialog.loading = false
                })
            },
            __veTableCloseFieldSettingsDialog() {
                this.__veTableInvalidateFieldSettingDialogRequest()
                this.fieldSettingDialog.visible = false
                this.fieldSettingDialog.loading = false
                this.fieldSettingDialog.rows = []
                this.fieldSettingDialog.conflicts = []
                this.fieldSettingDialog.error = ''
                this.fieldSettingDialog.refreshError = false
            },
            closeFieldSettingsDialog() {
                return this.__veTableCloseFieldSettingsDialog()
            },
            __veTableRestoreFieldSettingsDialog() {
                if (this.fieldSettingDialog.loading || this.fieldSettingDialog.refreshError) {
                    return cloneRows(this.fieldSettingDialog.rows)
                }
                const config = this.getFieldSettingConfig(this.fieldSettingDialog.key)
                const state = this.getFieldSettingState(config.key)
                const result = resetFieldSettings(
                    configurableMetadataRows(this, config), state.storedRows, this.getFieldSettingScope(config.key),
                    {
                        lockedFieldKeys: this.getFieldSettingLockedKeys(config.key),
                        fixedFieldKeys: this.getFieldSettingFixedKeys(config.key),
                        getDisplayName: item => this.getFieldSettingDisplayName(item),
                    }
                )
                this.fieldSettingDialog.rows = this.decorateFieldSettingRows(result.rows, config.key)
                this.fieldSettingDialog.conflicts = (result.conflicts || []).slice()
                this.fieldSettingDialog.error = ''
                this.fieldSettingDialog.refreshError = false
                return cloneRows(this.fieldSettingDialog.rows)
            },
            restoreFieldSettingsDialog() {
                return this.__veTableRestoreFieldSettingsDialog()
            },
            resetFieldSettingsDialog() {
                return this.__veTableRestoreFieldSettingsDialog()
            },
            __veTableSaveFieldSettings(configKey, rows) {
                const config = this.getFieldSettingConfig(configKey)
                const state = this.getFieldSettingState(config.key)
                const version = this.beginFieldSettingRequest(config.key)
                state.loading = true
                return this.getFormFieldSettingsService().save(
                    this.getFieldSettingScope(config.key), rows || state.rows, this.getFieldSettingApiContext(),
                    {
                        lockedFieldKeys: this.getFieldSettingLockedKeys(config.key),
                        fixedFieldKeys: this.getFieldSettingFixedKeys(config.key),
                        conflicts: state.conflicts,
                        allowedStoredRows: state.storedRows,
                    }
                ).then(result => {
                    if (!this.isCurrentFieldSettingRequest(config.key, version)) return Object.assign({}, result, { stale: true })
                    state.rows = this.decorateFieldSettingRows(result.rows, config.key)
                    state.storedRows = cloneRows(result.storedRows || result.rows)
                    state.runtimeWidthMap = {}
                    state.conflicts = (result.conflicts || []).slice()
                    this.syncFieldSettingState(config.key)
                    this.__veTableApplyFieldSettingRows(config.key)
                    return Object.assign({}, result, { stale: false })
                }).catch(error => {
                    if (!this.isCurrentFieldSettingRequest(config.key, version)) return { stale: true, error }
                    throw error
                }).finally(() => {
                    if (this.isCurrentFieldSettingRequest(config.key, version)) state.loading = false
                })
            },
            saveFieldSettings(configKey, rows) {
                return this.__veTableSaveFieldSettings(configKey, rows)
            },
            saveFieldSettingsDialog(rows) {
                const configKey = this.fieldSettingDialog.key
                const draftRows = rows || this.fieldSettingDialog.rows
                if (this.fieldSettingDialog.loading || this.fieldSettingDialog.refreshError || !draftRows || !draftRows.length) return Promise.resolve(null)
                const lockedRows = normalizeSortBy(sanitizeFieldSettings(draftRows, this.getFieldSettingLockedKeys(configKey)))
                const version = this.__veTableBeginFieldSettingDialogRequest(configKey)
                this.fieldSettingDialog.loading = true
                this.fieldSettingDialog.error = ''
                this.fieldSettingDialog.refreshError = false
                return this.__veTableSaveFieldSettings(configKey, lockedRows).then(result => {
                    if (result.stale || !this.__veTableIsCurrentFieldSettingDialogRequest(configKey, version)) return result
                    if (result.refreshError) {
                        const refreshMessage = '字段设置已保存，但配置刷新失败，请关闭后重新打开。'
                        this.fieldSettingDialog.error = { type: 'refresh', message: refreshMessage }
                        this.fieldSettingDialog.refreshError = true
                        if (this.$message && typeof this.$message.warning === 'function') this.$message.warning(refreshMessage)
                        return result
                    }
                    this.__veTableCloseFieldSettingsDialog()
                    return result
                }).catch(error => {
                    if (this.fieldSettingDialog.visible && this.__veTableIsCurrentFieldSettingDialogRequest(configKey, version)) {
                        const message = error.message || '字段设置保存失败。'
                        this.fieldSettingDialog.error = message
                        if (this.$message && typeof this.$message.error === 'function') this.$message.error(message)
                    }
                    return null
                }).finally(() => {
                    if (this.__veTableIsCurrentFieldSettingDialogRequest(configKey, version)) this.fieldSettingDialog.loading = false
                })
            },
        },
    }
}
